import React, { useRef, useState, useEffect } from "react";
import PropTypes from "kf-proptypes/widgets";
import { t } from "@lingui/macro";

import {
  BaseDropdown,
  BASE_DROPDOWN_POSITION,
  Menu,
  MenuItem
} from "@widgets/inputs/dropdown/index.jsx";
import { Icon, ICON_TYPES } from "@widgets/datadisplay/index.jsx";
import { DownArrowFilled as DownArrowFilledIcon } from "../../icons/index.jsx";

import styles from "./flowtab.css";

FlowTabs.propTypes = {
  /** A list of <FlowTab /> components.*/
  children: PropTypes.node.isRequired,
  /** Id of the currently choosen tab. */
  activeTabId: PropTypes.string.isRequired,
  /** A callback function that gets invoked when the tab changes. */
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  /** Wheather to show the text which will serve as the description for the dropdown menu to select hidden tabs. */
  showMoreTabsText: PropTypes.bool,
  /** A custom text, which will serve as the description for the dropdown menu to select hidden tabs.  */
  moreTabsText: PropTypes.string,
  /** The maximum number of visible tabs. The rest will be available in the dropdown. */
  maxVisibleTabs: PropTypes.number
};

FlowTabs.defaultProps = {
  showMoreTabsText: true,
  moreTabsText: "More"
};
export function FlowTabs(props) {
  const {
    children,
    activeTabId,
    onChange,
    className,
    showMoreTabsText = true,
    moreTabsText = t`More`,
    maxVisibleTabs = 6
  } = props;
  const [tabItems, setTabItems] = useState([]);
  const [inkStyle, setInkStyle] = useState({});
  const [activeTabRef, setActiveTabRef] = useState(undefined);

  useEffect(
    function updateTabLength() {
      if (tabItems.length !== children.length) {
        setTabItems(children.map((item) => ({ ...item.props })));
      } else {
        children.forEach((tabItem) => {
          let tab = tabItems.find((item) => item.tabId === tabItem.props.tabId);
          tab && Object.assign(tab, tabItem.props);
        });
        setTabItems([...tabItems]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [children]
  );

  useEffect(
    function updateActiveTabRef() {
      if (activeTabRef) {
        setTabBarStyle(activeTabRef);
      }
    },
    [activeTabRef]
  );

  const setTabBarStyle = (element) => {
    if (element) {
      let tabOffset = {
        left: element.offsetLeft,
        width: element.offsetWidth
      };
      setInkStyle(tabOffset);
    }
  };

  const onTabChange = (tabId) => {
    onChange && onChange(tabId);
  };

  const onDropDownTabClick = (tabId) => {
    swapTabItems(tabId);
    onChange && onChange(tabId);
  };

  const swapTabItems = (tabId) => {
    let selectedTabIndex = tabItems.findIndex((item) => item.tabId === tabId);
    let tabs = tabItems.slice();
    var temp = tabs[maxVisibleTabs - 1];
    tabs[maxVisibleTabs - 1] = tabs[selectedTabIndex];
    tabs[selectedTabIndex] = temp;
    setTabItems(tabs);
  };

  useEffect(
    function updateActiveTabInDropdown() {
      var isActiveTabInDropdown = tabItems
        .slice(maxVisibleTabs, tabItems.length)
        .find((item) => item.tabId === activeTabId);
      if (isActiveTabInDropdown) {
        swapTabItems(isActiveTabInDropdown.tabId);
      }
      setTabBarStyle(activeTabRef);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabItems, activeTabId]
  );

  return (
    <div className={`${styles.flowTabsContainer} ${className}`}>
      <ul className={styles.flowTabs}>
        {tabItems.slice(0, maxVisibleTabs).map((child, i) => {
          return (
            <FlowTabItem
              key={i.toString()}
              onClick={onTabChange}
              title={child.title}
              tabId={child.tabId}
              isActive={activeTabId === child.tabId}
              tabsLength={
                tabItems.length < 6 ? tabItems.length - 1 : maxVisibleTabs
              }
              setActiveTabRef={setActiveTabRef}
              activeTabId={activeTabId}
              {...child}
            >
              {child.children}
            </FlowTabItem>
          );
        })}
        {tabItems.length > maxVisibleTabs && (
          <BaseDropdown
            targetRenderer={() => {
              return (
                <div className={styles.moreTabsTargetContainer}>
                  {showMoreTabsText && (
                    <div className={styles.moreTabsText}>{moreTabsText}</div>
                  )}
                  <Icon type={ICON_TYPES.TYPE3} clickable={true}>
                    <DownArrowFilledIcon />
                  </Icon>
                </div>
              );
            }}
            position={BASE_DROPDOWN_POSITION.BOTTOM_RIGHT}
            listRenderer={({ closePopup }) => {
              return (
                <Menu>
                  {tabItems
                    .slice(maxVisibleTabs, tabItems.length)
                    .map((menu) => {
                      return (
                        <MenuItem
                          className={styles.flowTabExtraTabItem}
                          key={menu.tabId}
                          isSelected={activeTabId === menu.tabId}
                          onClick={() => {
                            onDropDownTabClick(menu.tabId);
                            closePopup();
                          }}
                          icon={
                            menu.prefixIcon && (
                              <span className={styles.flowTabExtraTabItemIcon}>
                                {menu.prefixIcon}
                              </span>
                            )
                          }
                        >
                          {menu.title}
                        </MenuItem>
                      );
                    })}
                </Menu>
              );
            }}
          />
        )}

        <div className={styles.tabInkBar} style={inkStyle} />
      </ul>
    </div>
  );
}

function FlowTabItem(props) {
  const {
    onClick,
    title,
    tabId,
    isActive,
    tabsLength,
    setActiveTabRef,
    activeTabId,
    prefixIcon,
    ...restProps
  } = props;

  const activeTabRef = useRef(undefined);

  const onTabClick = (e) => {
    onClick(tabId);
  };

  useEffect(function updateActiveTabRef() {
    if (isActive) {
      setActiveTabRef(activeTabRef.current);
    }
  });
  return (
    <li
      key={tabId}
      // style={{ maxWidth: `calc(100% / ${tabsLength})` }}
      className={`${styles.flowTabItem} ${
        isActive ? styles.flowTabActive : ""
      } ${prefixIcon ? styles.flowTabItemWithIcon : ""}`}
      onClick={onTabClick}
      ref={(ref) => {
        if (isActive) activeTabRef.current = ref;
      }}
      {...restProps}
    >
      {prefixIcon && (
        <Icon paddingNone={true} className={styles.flowTabIcon}>
          {prefixIcon}
        </Icon>
      )}
      <span>{title}</span>
    </li>
  );
}

FlowTabItem.propTypes = {
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  tabId: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  tabsLength: PropTypes.number.isRequired,
  setActiveTabRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  activeTabId: PropTypes.string.isRequired,
  prefixIcon: PropTypes.node
};

export function FlowTab(props) {
  const { children } = props;
  return children;
}

FlowTab.propTypes = {
  children: PropTypes.node,
  tabId: PropTypes.string
};
